import { useCallback } from "react";
import { resolveSceneObjectEditSession } from "../../app/editSession.ts";
import { useStore } from "../../app/store.ts";
import { topologyFaceCount } from "../../modeling/topology.ts";
import type { EditGranularity } from "../../modeling/types.ts";
import { SidebarCard } from "../SidebarCard.tsx";
import { SectionLabel, SegmentedControl, SliderRow } from "../shared.tsx";
import { COLOR_TEXT_SUBTLE, FONT_SIZE_SMALL, SPACE_MD } from "../tokens.ts";

const GRANULARITY_OPTIONS: {
  label: string;
  value: EditGranularity;
}[] = [
  { value: "vertex", label: "Vertex" },
  { value: "edge", label: "Edge" },
  { value: "face", label: "Face" },
];

export function EditModeInfo() {
  const editMode = useStore((s) => s.editMode);
  const baseObj = useStore((s) =>
    editMode ? s.sceneObjects.get(editMode.objectId) : undefined,
  );
  const activeEditSession = useStore((s) =>
    editMode?.objectId && s.activeEditSession?.objectId === editMode.objectId
      ? s.activeEditSession
      : null,
  );
  const setEditGranularity = useStore((s) => s.setEditGranularity);
  const setSkinRadii = useStore((s) => s.setSkinRadii);

  const handleSkinRadiusChange = useCallback(
    (value: number) => {
      if (!editMode || !baseObj) return;
      const obj = resolveSceneObjectEditSession(baseObj, activeEditSession);
      const vc = obj.vertices.length / 3;
      const newRadii = obj.skinRadii
        ? new Float32Array(obj.skinRadii)
        : new Float32Array(vc).fill(1);
      for (const vi of editMode.selectedVertices) {
        newRadii[vi] = Math.max(0, value);
      }
      setSkinRadii(editMode.objectId, newRadii);
    },
    [editMode, baseObj, activeEditSession, setSkinRadii],
  );

  if (!editMode) return null;
  if (!baseObj) return null;
  const obj = resolveSceneObjectEditSession(baseObj, activeEditSession);

  const isCurve = editMode.isCurve ?? false;
  const vertexCount = isCurve
    ? (obj.curveData?.points.length ?? 0)
    : obj.vertices.length / 3;
  const faceCount = isCurve ? 0 : topologyFaceCount(obj.topology);
  const selectedCount = editMode.selectedVertices.size;

  // Compute average skin radius for selected vertices (displayed in slider)
  const selectedHasVerts = !isCurve && selectedCount > 0;
  let avgSkinRadius = 1;
  if (selectedHasVerts) {
    let total = 0;
    for (const vi of editMode.selectedVertices) {
      total += obj.skinRadii ? obj.skinRadii[vi] : 1;
    }
    avgSkinRadius = total / selectedCount;
  }

  return (
    <SidebarCard>
      {/* Granularity selector — hidden for curves */}
      {!isCurve && (
        <>
          <SectionLabel>Select Mode</SectionLabel>
          <div style={{ marginBottom: SPACE_MD }}>
            <SegmentedControl
              options={GRANULARITY_OPTIONS}
              value={editMode.granularity}
              onChange={setEditGranularity}
            />
          </div>
        </>
      )}

      {/* Mesh/Curve stats */}
      <SectionLabel>{isCurve ? "Curve Info" : "Mesh Info"}</SectionLabel>
      <div
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: COLOR_TEXT_SUBTLE,
        }}
      >
        {isCurve ? (
          <>
            {vertexCount} points &middot; {selectedCount} sel
          </>
        ) : (
          <>
            {vertexCount} verts &middot; {faceCount} faces &middot;{" "}
            {selectedCount} sel
          </>
        )}
      </div>

      {/* Skin radius — only for mesh objects with vertices selected */}
      {selectedHasVerts && (
        <div style={{ marginTop: SPACE_MD }}>
          <SectionLabel>Skin Radius</SectionLabel>
          <SliderRow
            label="Scale"
            value={avgSkinRadius}
            min={0}
            max={5}
            step={0.01}
            displayValue={avgSkinRadius.toFixed(2)}
            onChange={handleSkinRadiusChange}
            defaultValue={1}
          />
        </div>
      )}
    </SidebarCard>
  );
}
